import React, { createContext, useEffect, useMemo } from 'react';
import { Reducer, useImmerReducer } from 'use-immer';
import debugCore from 'debug';

const BASE = 'da-portals';
// eslint-disable-next-line no-shadow
enum LogLevel {
  SILLY = 'silly',
  VERBOSE = 'verbose',
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}
const COLOURS = {
  silly: '#c228a0',
  verbose: '#999999',
  debug: '#619365',
  info: '#4ab1b6',
  warn: '#b86b27',
  error: '#a82e22'
}; // choose better colours :)

export type Settings = {
  logLevel: LogLevel;
  sources: string[];
};

export interface Logger {
  silly: (log: LogStructure) => void;
  verbose: (log: LogStructure) => void;
  debug: (log: LogStructure) => void;
  info: (log: LogStructure) => void;
  warn: (log: LogStructure) => void;
  error: (log: LogStructure) => void;
}

export type LogStateContextType = Settings & {};

export type LogDispatchContextType = {
  setSettings: (settings: Settings) => void;
  log: Logger;
};

type LogContextProps = {
  children: React.ReactNode;
};

export const LogStateContext = createContext({} as LogStateContextType);
export const LogDispatchContext = createContext({} as LogDispatchContextType);

const eventReducer: Reducer<LogStateContextType, any> = (draft, action) => {
  switch (action.type) {
    case 'setSettings': {
      draft.logLevel = action.settings.logLevel || LogLevel.INFO;
      draft.sources = action.settings.sources || [];
      break;
    }
    default: {
      throw new Error(`Unhandled dispatch: ${action.type}`);
    }
  }
};

const initialState: LogStateContextType = {
  logLevel: LogLevel.INFO,
  sources: ['EnhancedAnalyticsContext', 'AuthContext', 'CurrencyContext', 'UserCurrencyComponent']
};

export interface LogStructure {
  source: string;
  action: string;
  message?: string;
  data?: any;
  error?: Error;
}

interface GenerateLog extends LogStructure {
  level: string;
}

export function LogProvider({ children }: LogContextProps): any {
  const [state, dispatch] = useImmerReducer<any, any>(eventReducer, initialState);

  useEffect(() => {
    // Logger setup
    if (process.env.NODE_ENV !== 'production') {
      localStorage.setItem('debug', 'da-portals:*');
    }
  }, []);
  const setSettings = (newSettings: Settings) => {
    dispatch({ type: 'setSettings', settings: newSettings });
  };

  const generateMessage = ({ source, level, action, message, error, data }: GenerateLog) => {
    if (!state.sources.includes(source)) {
      return;
    }
    // Set the prefix which will cause debug to enable the message
    const namespace = `${BASE}:${level}`;
    const createDebug = debugCore(namespace);

    // Set the colour of the message based on the level
    createDebug.color = COLOURS[level];

    const logObject: any = {};
    if (message) {
      logObject.message = message;
    }
    if (error) {
      logObject.error = error;
    }
    if (data) {
      logObject.data = data;
    }
    createDebug(`${source}:${action}`, logObject);
  };

  const log = {
    silly: (logOptions: LogStructure) => generateMessage({ ...logOptions, level: 'silly' }),
    verbose: (logOptions: LogStructure) => generateMessage({ ...logOptions, level: 'verbose' }),
    debug: (logOptions: LogStructure) => generateMessage({ ...logOptions, level: 'debug' }),
    info: (logOptions: LogStructure) => generateMessage({ ...logOptions, level: 'info' }),
    warn: (logOptions: LogStructure) => generateMessage({ ...logOptions, level: 'warn' }),
    error: (logOptions: LogStructure) => generateMessage({ ...logOptions, level: 'error' })
  };

  const dispatchContextValue = useMemo(
    () => ({
      setSettings,
      log
    }),
    [setSettings]
  );

  return (
    <LogDispatchContext.Provider value={dispatchContextValue}>
      <LogStateContext.Provider value={state}>{children}</LogStateContext.Provider>
    </LogDispatchContext.Provider>
  );
}
